import React, { useState, useEffect, useCallback } from 'react';
import './gallery.css';

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});

  const galleryImages = [
    { id: 4, src: '/room2-2.jpeg', title: 'Premium Design', category: 'Interior' },
    { id: 5, src: '/room2.png', title: 'Relaxing Atmosphere', category: 'Ambiance', large: true },
    { id: 1, src: '/rom1-1.jpeg', title: 'Luxury Interior', category: 'Interior', large: true },
    { id: 2, src: '/room1.jpeg', title: 'Modern Comfort', category: 'Living' },
    { id: 3, src: '/room2-1.jpeg', title: 'Elegant Space', category: 'Dining' },
    { id: 6, src: '/wallart.jpeg', title: 'Artful Details', category: 'Decor' },
    // { id: 7, src: '/room3.jpeg', title: 'Natural Light', category: 'Ambiance' },
    { id: 8, src: '/main gate.jpeg', title: 'Grand Entrance', category: 'Interior' },
  ];

  // Preload images
  useEffect(() => {
    const imagePromises = galleryImages.map((img) => {
      return new Promise((resolve) => {
        const image = new Image();
        image.src = img.src;
        image.onload = () => {
          setLoadedImages(prev => ({ ...prev, [img.id]: true }));
          resolve();
        };
        image.onerror = () => resolve();
      });
    });
    Promise.all(imagePromises).then(() => setIsLoading(false));
  }, []);

  const handleImageClick = (img, index) => {
    setSelectedImage(img);
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setSelectedIndex(null);
    document.body.style.overflow = 'auto';
  };

  const navigate = useCallback((direction) => {
    const newIndex = direction === 'next'
      ? (selectedIndex + 1) % galleryImages.length
      : (selectedIndex - 1 + galleryImages.length) % galleryImages.length;
    setSelectedImage(galleryImages[newIndex]);
    setSelectedIndex(newIndex);
  }, [selectedIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigate('prev');
      if (e.key === 'ArrowRight') navigate('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, navigate]);

  // Calculate optimal grid layout using CSS Grid with auto-fit
  const getItemSizeClass = (img, index) => {
    if (img.large) return 'grid-item-large';
    if (index % 5 === 1) return 'grid-item-tall';
    if (index % 7 === 3) return 'grid-item-wide';
    return 'grid-item';
  };

  return (
    <div className="gallery-container">

      {/* HERO SECTION - Parallax Effect */}
      <section className="gallery-hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${galleryImages[0].src})` }} />
        <div className="hero-overlay" />
        <div className="hero-content">
        
          <h1 className="hero-title">
            Visual <span className="hero-accent">Gallery</span>
          </h1>
          <div className="hero-divider" />
          <p className="hero-subtitle">
            Your Journey to Wellness Begins with a Single Step. Explore Our Gallery of Transformative Spaces and Inspiring Designs.
          </p>
          <div className="hero-stats">
            <div className="stat"><span>20+</span><span>Years Excellence</span></div>
            <div className="stat"><span>9k+</span><span>Happy Clients</span></div>
            <div className="stat"><span>10+</span><span>Wellness Programs</span></div>
          </div>
          <button className="hero-cta-gallery" onClick={() => document.querySelector('.gallery-grid').scrollIntoView({ behavior: 'smooth' })}>
            Explore 
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
        <div className="hero-scroll-indicator">
          <span>Scroll</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* MASONRY GRID SECTION */}
      <section className="gallery-grid-section">


        {isLoading && (
          <div className="loader-container">
            <div className="loader" />
            <p>Loading masterpieces...</p>
          </div>
        )}

        <div className="gallery-grid" style={{ opacity: isLoading ? 0 : 1 }}>
          {galleryImages.map((img, idx) => (
            <div
              key={img.id}
              className={`gallery-item ${getItemSizeClass(img, idx)} ${loadedImages[img.id] ? 'loaded' : ''}`}
              onClick={() => handleImageClick(img, idx)}
              style={{ animationDelay: `${idx * 0.05}s` }}
            >
              <div className="gallery-item-inner">
                <img 
                  src={img.src} 
                  alt={img.title}
                  loading="lazy"
                />
                <div className="gallery-overlay">
                  <div className="overlay-content">
                    <span className="image-category">{img.category}</span>
                    <h3 className="image-title">{img.title}</h3>
                    <div className="image-hover-line" />
                    {/* <span className="view-btn">View Project →</span> */}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROFESSIONAL LIGHTBOX */}
      {selectedImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); navigate('prev'); }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); navigate('next'); }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-image-container">
              <img src={selectedImage.src} alt={selectedImage.title} />
              <div className="lightbox-counter">
                {selectedIndex + 1} / {galleryImages.length}
              </div>
            </div>
            <div className="lightbox-info">
              <div className="lightbox-category">{selectedImage.category}</div>
              <h2 className="lightbox-title">{selectedImage.title}</h2>
              <div className="lightbox-divider" />
              <p className="lightbox-description">
                Experience the perfect blend of form and function. Every detail meticulously crafted
                to create spaces that inspire and endure.
              </p>
              {/* <button className="lightbox-inquire">Inquire About This Project</button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;